"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import { cn } from "@/lib/utils";

// 规格项数据结构
interface SpecItem {
	labelKey: string;
	valueKey: string;
}

// 表面处理项数据结构
export interface FinishItem {
	titleKey: string;
	descKey: string;
	image: string;
	specs: SpecItem[];
}

// 默认图片路径
const DEFAULT_IMAGE_BASE_PATH = "/image/new-version/machining/gear-machining/finish";

// 默认表面处理数据
const DEFAULT_FINISH_ITEMS: FinishItem[] = [
	{
		titleKey: "carburizingTitle",
		descKey: "carburizingDesc",
		image: "carburizing-hardening.webp",
		specs: [
			{ labelKey: "carburizingSpec1Label", valueKey: "carburizingSpec1Value" },
			{ labelKey: "carburizingSpec2Label", valueKey: "carburizingSpec2Value" },
			{ labelKey: "carburizingSpec3Label", valueKey: "carburizingSpec3Value" },
		],
	},
	{
		titleKey: "nitridingTitle",
		descKey: "nitridingDesc",
		image: "nitriding.webp",
		specs: [
			{ labelKey: "nitridingSpec1Label", valueKey: "nitridingSpec1Value" },
			{ labelKey: "nitridingSpec2Label", valueKey: "nitridingSpec2Value" },
			{ labelKey: "nitridingSpec3Label", valueKey: "nitridingSpec3Value" },
		],
	},
	{
		titleKey: "inductionTitle",
		descKey: "inductionDesc",
		image: "induction-hardening.webp",
		specs: [
			{ labelKey: "inductionSpec1Label", valueKey: "inductionSpec1Value" },
			{ labelKey: "inductionSpec2Label", valueKey: "inductionSpec2Value" },
			{ labelKey: "inductionSpec3Label", valueKey: "inductionSpec3Value" },
		],
	},
	{
		titleKey: "phosphatingTitle",
		descKey: "phosphatingDesc",
		image: "phosphating.webp",
		specs: [
			{ labelKey: "phosphatingSpec1Label", valueKey: "phosphatingSpec1Value" },
			{ labelKey: "phosphatingSpec2Label", valueKey: "phosphatingSpec2Value" },
			{ labelKey: "phosphatingSpec3Label", valueKey: "phosphatingSpec3Value" },
		],
	},
	{
		titleKey: "electroplatingTitle",
		descKey: "electroplatingDesc",
		image: "electroplating.webp",
		specs: [
			{ labelKey: "electroplatingSpec1Label", valueKey: "electroplatingSpec1Value" },
			{ labelKey: "electroplatingSpec2Label", valueKey: "electroplatingSpec2Value" },
			{ labelKey: "electroplatingSpec3Label", valueKey: "electroplatingSpec3Value" },
		],
	},
	{
		titleKey: "shotPeeningTitle",
		descKey: "shotPeeningDesc",
		image: "carburizing-hardening.webp",
		specs: [
			{ labelKey: "shotPeeningSpec1Label", valueKey: "shotPeeningSpec1Value" },
			{ labelKey: "shotPeeningSpec2Label", valueKey: "shotPeeningSpec2Value" },
			{ labelKey: "shotPeeningSpec3Label", valueKey: "shotPeeningSpec3Value" },
		],
	},
];

export interface FinishProps {
	translationNamespace?: string;
	imageBasePath?: string;
	finishItems?: FinishItem[];
	className?: string;
}

export default function Finish({
	translationNamespace = "gearFinish",
	imageBasePath = DEFAULT_IMAGE_BASE_PATH,
	finishItems = DEFAULT_FINISH_ITEMS,
	className,
}: FinishProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className={cn("py-8 sm:py-10 md:py-12 lg:py-16", className)}>
			<div className="container">
				{/* 标题区域 */}
				<div className="mx-auto mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<p className="mx-auto text-sm leading-relaxed text-gray-900 md:text-base">{t("subtitle")}</p>
				</div>

				{/* 卡片网格 */}
				<div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
					{finishItems.map((item, index) => (
						<div
							key={index}
							className="group flex flex-col overflow-hidden rounded-lg border bg-white shadow-[0_8px_24px_rgba(0,0,0,0.12)] transition-shadow duration-300"
						>
							{/* 图片区域 */}
							<div className="relative w-full overflow-hidden">
								<Image
									src={`${imageBasePath}/${item.image}`}
									alt={t(item.titleKey)}
									width={1000}
									height={1000}
									className="max-h-[280px] w-full object-contain"
									sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
								/>
							</div>

							{/* 内容区域 */}
							<div className="flex flex-1 flex-col p-5 group-hover:bg-[#f6ffde] sm:p-6">
								{/* 标题 */}
								<h3 className="mb-3 text-lg font-bold text-black sm:text-xl md:text-2xl">
									{t(item.titleKey)}
								</h3>

								{/* 描述 */}
								<p className="mb-5 text-sm leading-relaxed text-gray-900 md:text-base">{t(item.descKey)}</p>

								{/* 技术规格 */}
								<div className="mt-auto">
									<h4 className="mb-3 text-base font-semibold text-black sm:text-lg">{t("specsTitle")}</h4>
									<div className="space-y-4 rounded-xl bg-white p-4">
										{item.specs.map((spec, specIndex) => (
											<div
												key={specIndex}
												className="flex items-start justify-between gap-4 border-b border-[#c7c7c7] pb-2 last:border-b-0 last:pb-0"
											>
												<span className="text-sm text-black">{t(spec.labelKey)}</span>
												<span className="text-right text-sm font-medium text-black">{t(spec.valueKey)}</span>
											</div>
										))}
									</div>
								</div>
							</div>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
